// Measurement overlay payload — the shape Unity sends via the WebBridge
// "SetupMeasurement" event. Unity projects the setup's bounding edges to screen
// space (CSS pixels, top-left origin, Y already flipped) and we draw an SVG
// overlay aligned to those points. See MeasurementOverlay for the rendering.
//
// Unity fires this only when the measurement changes (camera move, product
// add/remove/move/resize) and dedups identical frames, so the handler just
// stashes the latest payload — it does NOT fire every frame. Render from the
// last payload and update on the next; never clear on "no event this frame".

/** One projected screen-space endpoint. `onScreen` is false when the point is
 *  behind the camera, in which case x/y are meaningless. */
export interface MeasurementPoint {
  x: number;
  y: number;
  onScreen: boolean;
}

/** Which way the label should be offset from its line — Unity computes this
 *  from the setup's orientation so the tag lands outside the bounding box. */
export type MeasurementSide = "top" | "bottom" | "left" | "right";

/** A measured edge: two screen-space endpoints + its real length in metres.
 *  `onScreen` is false when either endpoint is behind the camera — skip drawing
 *  the line entirely in that case. `side` (optional; older builds omit it) is
 *  the direction to push the label. */
export interface MeasurementLine {
  from: MeasurementPoint;
  to: MeasurementPoint;
  meters: number;
  side?: MeasurementSide;
  onScreen: boolean;
}

/** The full "setup" payload. `dimensions` is the world-axis-aligned size in
 *  metres; `widthLine` is the top-front horizontal edge (left→right) and
 *  `heightLine` the vertical edge nearest the camera (top→bottom). `viewport`
 *  is Unity's render size in pixels — scale endpoints by overlay-size ÷ viewport
 *  when the overlay's pixel size differs from Unity's render resolution. */
export interface MeasurementSetup {
  kind: "setup";
  dimensions: { width: number; height: number; depth: number };
  widthLine: MeasurementLine;
  heightLine: MeasurementLine;
  viewport: { width: number; height: number };
}

/** The teardown payload — scene is empty, hide the overlay. */
export interface MeasurementNone {
  kind: "none";
}

export type MeasurementPayload = MeasurementSetup | MeasurementNone;

/** Format a number to a fixed number of decimals using the given decimal
 *  separator ("." or ","). Used for end-user-facing measurement readouts so a
 *  Designer can show "72,0" instead of "72.0". Only the decimal mark is swapped;
 *  no thousands grouping is applied (measurements here are small cm values). A
 *  separator other than "," is treated as "." (the default). */
export function formatDecimal(value: number, decimals: number, separator: string): string {
  const fixed = value.toFixed(decimals);
  return separator === "," ? fixed.replace(".", ",") : fixed;
}

/** Parse the raw "SetupMeasurement" JSON into a typed payload. Guards on `kind`
 *  first (the "none" payload omits dimensions/lines). Returns null on malformed
 *  JSON or an unexpected shape so callers can ignore it without throwing. */
export function parseMeasurementPayload(json: string): MeasurementPayload | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!raw || typeof raw !== "object") return null;
  const kind = (raw as { kind?: unknown }).kind;
  if (kind === "none") return { kind: "none" };
  if (kind !== "setup") return null;

  const m = raw as Partial<MeasurementSetup>;
  // Minimal shape guard — both lines + viewport must be present for "setup".
  if (!m.widthLine || !m.heightLine || !m.viewport || !m.dimensions) return null;
  return {
    kind: "setup",
    dimensions: m.dimensions,
    widthLine: m.widthLine,
    heightLine: m.heightLine,
    viewport: m.viewport,
  };
}
